// @ai-generated 2026-09-03T16:40:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import { useHasSystemAccess } from '@Contexts/session/useHasSystemAccess';
import { useTransparentRef } from '@Hooks/useTransparentRef';
import { type ApiValidationRuleset } from '@Model';
import { json as jsonLang } from '@codemirror/lang-json';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useTranslate,
} from '@synqo/frontend-component-library';
import CodeMirror from '@uiw/react-codemirror';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { compactAst, isSameAst, prettyPrintAst } from './astJson';
import { RulesetPreview } from './RulesetPreview';

interface RulesetEditFrameProps extends IFrameEditShellProps<ApiValidationRuleset> {
    /** Base URL of the owning service's <c>/rulesets</c> endpoint (e.g. <c>AUFTRAG_API + '/rulesets'</c>). */
    apiPath: string;
    /** Cache key so the two service variants don't share a single resource-cache slot. */
    resourceCacheKey: string;
    /** The owning service's ruleset write permissions — the prefix differs per service. */
    createPermission: string;
    updatePermission: string;
    deletePermission: string;
}

/**
 * Detail view for a validation ruleset.
 *
 * Rulesets are one catalogue shared by every mandant, so authoring is reserved for the seeding
 * client.
 */
export function RulesetEditFrame(props: RulesetEditFrameProps) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const hasPermission = useHasPermission();
    const hasSystemAccess = useHasSystemAccess();
    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    const renderer = useCallback(
        (bodyProps: IFrameBodyProps<ApiValidationRuleset>) => (
            <RulesetEditFrameBody {...bodyProps} apiPath={props.apiPath} resourceCacheKey={props.resourceCacheKey} />
        ),
        [props.apiPath, props.resourceCacheKey],
    );

    return (
        <FrameEditBase<ApiValidationRuleset>
            apiResourcePath={props.apiPath}
            renderer={renderer}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasSystemAccess && hasPermission(props.updatePermission)}
            canCreate={props.canCreate !== false && hasSystemAccess && hasPermission(props.createPermission)}
            canDelete={props.canDelete !== false && hasSystemAccess && hasPermission(props.deletePermission)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

interface RulesetEditFrameBodyProps extends IFrameBodyProps<ApiValidationRuleset> {
    apiPath: string;
    resourceCacheKey: string;
}

function RulesetEditFrameBody(props: RulesetEditFrameBodyProps) {
    const i18n = useTranslate('');

    const isViewMode = props.mode === EditFrameMode.View;

    const binder = useGuiBinder<ApiValidationRuleset>({
        ...props,
        canEdit: !isViewMode,
    });

    const jsonExtensions = useMemo(() => [jsonLang()], []);
    const { onChange: onAstChange, onConfirm: onAstConfirm, locked: astLocked } = binder.bindToProp('ast_json');

    const prettyAst = useMemo(() => prettyPrintAst(props.data.ast_json), [props.data.ast_json]);
    const ast = useTransparentRef(prettyAst, isViewMode);

    const handleAstChange = useCallback(
        (value: string) => {
            if (!onAstChange(value, true)) {
                return;
            }

            ast.current = value;
            return true;
        },
        [onAstChange, ast],
    );

    const handleAstConfirm = useCallback(() => {
        const compacted = compactAst(ast.current);

        if (isSameAst(compacted, props.data.ast_json)) {
            return;
        }

        return onAstConfirm?.(compacted, true);
    }, [onAstConfirm, ast, props.data.ast_json]);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Regelsatz', props.mode),
                resourceLabel: 'Regelsatz',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="HeadData" label="Regelsatz">
                <Grid columns={12}>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp<string>('key')} label={i18n('Schlüssel')} />
                        <TextInput {...binder.bindToProp<string>('title')} label={i18n('Titel')} />
                        <TextInput {...binder.bindToProp<string>('entity_type')} label={i18n('Entitätstyp')} />
                    </GridColumns>
                    <GridColumn span={12}>
                        <TextInput
                            {...binder.bindToProp<string>('description')}
                            inputSize={3}
                            label={i18n('Beschreibung')}
                        />
                    </GridColumn>
                </Grid>
            </Card>
            {isViewMode ? (
                <Card name="Rules" label="Regeln">
                    <RulesetPreview
                        apiPath={props.apiPath}
                        resourceCacheKey={props.resourceCacheKey}
                        rulesetId={props.data.id}
                    />
                </Card>
            ) : (
                <Card name="Ast" label="Regeln (JSON)">
                    <CodeMirror
                        key={props.mode}
                        value={ast.current}
                        height="600px"
                        extensions={jsonExtensions}
                        onChange={handleAstChange}
                        onBlur={handleAstConfirm}
                        editable={!astLocked}
                    />
                </Card>
            )}
        </EditFrameShell>
    );
}
